/** @format */

import { useState } from 'react';
import {
  Box,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Popper,
} from '@mui/material';
import { TouchApp } from '@mui/icons-material';
import { formatRevenue } from '../../../../Util/utils';
import {
  REVENUE_COLORS,
  getPerformanceColor,
} from './DonutCard';

const ROW_ID_TO_TITLE = {
  room_revenue: 'Room',
  food_revenue: 'Food',
  bev_revenue: 'Beverage',
  other_fnb_revenue: 'F&B Others',
  spa_revenue: 'Spa',
  mod_revenue: 'MOD',
};

function getActColor(row) {
  const percentage = Number(row?.budget_percentage);

  if (!Number.isFinite(percentage)) {
    return 'inherit';
  }

  if (percentage < -5) {
    return 'red';
  }

  if (percentage >= 0) {
    return 'green';
  }

  return 'orange';
}

function formatTableValue(value) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  const numericValue =
    typeof value === 'string'
      ? Number(value.replace(/,/g, ''))
      : Number(value);

  if (!Number.isFinite(numericValue)) {
    return '-';
  }

  return formatRevenue(numericValue);
}

function TableCard({ data = [], totalRevenueData }) {
  const HEADERS = ['Act', 'Bud', 'Fcst', 'LY'];

  const rows = [
    ...data,
    ...(totalRevenueData
      ? [
        {
          ...totalRevenueData,
          id: 'total_revenue',
          title: 'Total Revenue',
        },
      ]
      : []),
  ];

  return (
    <Box
      sx={{
        borderRadius: '8px',
        width: '100%',
        minWidth: 0,
      }}
    >
      <TableContainer
        sx={{
          width: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Table
          sx={{
            width: '100%',
            minWidth: { xs: '100%', sm: 420 },

            tableLayout: {
              xs: 'auto',
              sm: 'auto',
            },

            borderRadius: 2,
            overflow: 'hidden',

            '& .MuiTableCell-root': {
              border: '1px solid rgba(224, 224, 224, 1)',

              padding: {
                xs: '5px 6px',
                sm: '8px 16px',
              },

              whiteSpace: 'nowrap',

              fontSize: {
                xs: '0.72rem',
                sm: '0.875rem',
              },
            },

            '& .MuiTableRow-root': {
              '&:nth-of-type(odd)': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
              },
            },

            '& .MuiTableHead-root .MuiTableCell-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              fontWeight: 'bold',
              border: 'none',
              borderRight: '2px solid #fff',
            },

            '& .MuiTableHead-root .MuiTableCell-root:last-child': {
              borderRight: 'none',
            },

            '& .MuiTableHead-root .MuiTableCell-root:first-of-type': {
              backgroundColor: 'transparent',
              border: 'none',
            },

            '& .MuiTableCell-body:first-child': {
              borderRadius: 0,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  border: 'none',
                  backgroundColor: 'transparent !important',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: {
                      xs: 0.5,
                      sm: 1,
                    },
                  }}
                >
                  <ColorCodeTooltip />

                  <Typography
                    sx={{
                      color: '#374151',
                      fontSize: {
                        xs: '0.72rem',
                        sm: '0.9rem',
                      },
                      fontWeight: 400,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Revenue in ’000s
                  </Typography>
                </Box>
              </TableCell>

              {HEADERS.map((header) => (
                <TableCell
                  align="center"
                  key={header}
                  sx={{
                    fontSize: {
                      xs: '0.72rem',
                      sm: '0.875rem',
                    },
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => {
              const revenueColor =
                REVENUE_COLORS[ROW_ID_TO_TITLE[row.id]];

              return (
                <TableRow key={row.id}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      fontWeight:
                        row.id === 'total_revenue'
                          ? 600
                          : 500,

                      ...(row.id === 'total_revenue'
                        ? {
                          backgroundColor:
                            getPerformanceColor(
                              row.budget_percentage
                            ),
                          color: '#fff',
                          borderColor: '#fff',
                        }
                        : revenueColor
                          ? {
                            backgroundColor:
                              revenueColor,
                            color: '#263238',
                            borderColor: '#fff',
                          }
                          : {}),
                    }}
                  >
                    {row.title}
                  </TableCell>

                  <TableCell align="right">
                    <span
                      className="data-number"
                      style={{
                        color: getActColor(row),
                        fontWeight: 500,
                        fontSize: 'inherit',
                      }}
                    >
                      {formatTableValue(row.value)}
                    </span>
                  </TableCell>

                  <TableCell align="right">
                    <span
                      className="data-number"
                      style={{
                        fontSize: 'inherit',
                      }}
                    >
                      {formatTableValue(row.budget)}
                    </span>
                  </TableCell>

                  <TableCell align="right">
                    <span
                      className="data-number"
                      style={{
                        fontSize: 'inherit',
                      }}
                    >
                      {formatTableValue(row.forecast)}
                    </span>
                  </TableCell>

                  <TableCell align="right">
                    <span
                      className="data-number"
                      style={{
                        fontSize: 'inherit',
                      }}
                    >
                      {formatTableValue(row.last_year)}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>


  );
}

function ColorCodeTooltip() {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMouseEnter = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        sx={{
          color: 'text.disabled',
          cursor: 'pointer',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <TouchApp
          fontSize="small"
          sx={{
            color: '#757575',
            fontSize: {
              xs: '15px',
              sm: '20px',
            },
          }}
        />
      </Box>

      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="right-start"
        sx={{
          zIndex: 9999,
        }}
      >
        <Box
          sx={{
            minWidth: 'fit-content',
            backgroundColor: '#e8e4e4',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 1,
            zIndex: 1300,
          }}
        >
          <Box sx={{ p: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                mb: 1,
              }}
            >
              Revenue Performance Information
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontWeight: 'medium',
                mb: 0.5,
              }}
            >
              Color Codes:
            </Typography>

            <Box sx={{ pl: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                }}
              >
                🔴 Red: Below 95%
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                }}
              >
                🟡 Amber: 95% - 100%
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                }}
              >
                🟢 Green: Above 100%
              </Typography>
            </Box>
          </Box>
        </Box>
      </Popper>
    </>


  );
}

export default TableCard;